'use client';

import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useUserProfile } from '../hooks/useUserProfile';
import { isLanguageSupported, getDefaultLanguage } from '../utils/languages';
import de from '../translations/de.json';
import el from '../translations/el.json';
import en from '../translations/en.json';
import es from '../translations/es.json';
import fr from '../translations/fr.json';
import hr from '../translations/hr.json';
import hu from '../translations/hu.json';
import it from '../translations/it.json';
import pt_BR from '../translations/pt_BR.json';
import ro from '../translations/ro.json';
import ru from '../translations/ru.json';
import sq from '../translations/sq.json';
import sr from '../translations/sr.json';
import tr from '../translations/tr.json';
import uk from '../translations/uk.json';

type LocaleContextType = {
  locale: string;
  messages: any;
  t: (key: string) => string;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const allMessages: Record<string, any> = {
  de,
  el,
  en,
  es,
  fr,
  hr,
  hu,
  it,
  pt_BR,
  ro,
  ru,
  sq,
  sr,
  tr,
  uk,
};

export default function DynamicLocaleProvider({ children }: { children: ReactNode }) {
  const { language, loading, user } = useUserProfile();

  const value = useMemo(() => {
    const defaultLocale = getDefaultLanguage();
    let effectiveLocale = defaultLocale;
    
    // Priority order:
    // 1. If user is authenticated and has a language preference, use that
    // 2. Otherwise, check localStorage (for unauthenticated users or temporary overrides)
    // 3. Otherwise, use default
    
    if (!loading && user && language && isLanguageSupported(language)) {
      // User is authenticated and has a valid language preference
      effectiveLocale = language;
    } else {
      // Try localStorage (works for both loading state and unauthenticated users)
      try {
        const storedLanguage = localStorage.getItem('language');
        if (storedLanguage && isLanguageSupported(storedLanguage)) {
          effectiveLocale = storedLanguage;
        }
      } catch {
        // localStorage may not be available, use default
      }
    }
    
    // Validate that the language is supported, fallback to default if not
    const locale = isLanguageSupported(effectiveLocale) ? effectiveLocale : defaultLocale;
    const messages = allMessages[locale] ?? allMessages[defaultLocale];
    
    return {
      locale,
      messages,
      t: (key: string) => {
        const translation = messages[key] || key;
        return translation;
      }
    };
  }, [language, loading, user]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside DynamicLocaleProvider');
  return context;
};